/**
 * SnapshotRenderer — renders Knowledge Hub page snapshots and execution
 * screenshots with image preview, detected elements, and metadata.
 */

import { Globe, Eye, MousePointer, FormInput, Cpu } from 'lucide-react'

import { useTestOpsCommandConfig } from '../../context'

interface ElementCounts {
  buttons?: number
  links?: number
  forms?: number
  inputs?: number
  images?: number
  headings?: Record<string, number>
  [k: string]: unknown
}

interface ButtonInfo {
  text?: string
  type?: string
}

interface FormField {
  name?: string
  type?: string
  label?: string
  required?: boolean
}

export function SnapshotRenderer({ data }: { data: Record<string, unknown> }) {
  const { ScreenshotComponent: ScreenshotImage } = useTestOpsCommandConfig()
  const screenshotUrl = (data.screenshot_url as string) || ''
  const pageTitle = (data.page_title as string) || ''
  const url = (data.url as string) || ''
  const portfolioName = (data.portfolio_name as string) || ''
  const status = (data.review_status as string) || (data.status as string) || ''
  const elementCounts = (data.element_counts as ElementCounts) || null
  const buttons = (data.buttons as ButtonInfo[]) || []
  const formFields = (data.form_fields as FormField[]) || []
  const framework = (data.framework as Record<string, boolean>) || null
  const knowledgeSummary = (data.knowledge_summary as string) || ''

  const detectedFrameworks = framework
    ? Object.entries(framework).filter(([, v]) => v).map(([k]) => k)
    : []

  return (
    <div className="space-y-4">
      {/* Screenshot */}
      {screenshotUrl && (
        <div className="rounded-lg border border-subtle overflow-hidden">
          <ScreenshotImage
            src={screenshotUrl}
            alt={pageTitle || 'Page screenshot'}
            className="w-full h-auto"
            loading="lazy"
          />
        </div>
      )}

      {/* Page info */}
      {url && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Globe className="w-3 h-3 shrink-0" />
          <span className="truncate">{url}</span>
        </div>
      )}

      {/* Status & portfolio */}
      <div className="flex flex-wrap items-center gap-2">
        {portfolioName && (
          <span className="rounded-full bg-info-soft border border-info/30 px-2 py-0.5 text-info-foreground font-medium">
            {portfolioName}
          </span>
        )}
        {status && (
          <span className={`rounded-full border px-2 py-0.5 font-medium ${
            status === 'approved' ? 'bg-success-soft border-success/30 text-success-foreground'
            : status === 'rejected' ? 'bg-destructive-soft border-destructive/30 text-destructive-foreground'
            : 'bg-foreground/[0.04] border-subtle text-muted-foreground'
          }`}>
            {status}
          </span>
        )}
        {detectedFrameworks.length > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Cpu className="w-3 h-3" />
            {detectedFrameworks.join(', ')}
          </span>
        )}
      </div>

      {/* Element counts */}
      {elementCounts && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Detected Elements
          </div>
          <div className="flex flex-wrap gap-2">
            {elementCounts.buttons != null && elementCounts.buttons > 0 && (
              <span className="rounded bg-accent-soft border border-accent/30 px-2 py-0.5 text-accent-soft-foreground">
                {elementCounts.buttons} buttons
              </span>
            )}
            {elementCounts.links != null && elementCounts.links > 0 && (
              <span className="rounded bg-info-soft border border-info/30 px-2 py-0.5 text-info-foreground">
                {elementCounts.links} links
              </span>
            )}
            {elementCounts.inputs != null && elementCounts.inputs > 0 && (
              <span className="rounded bg-info-soft border border-info/30 px-2 py-0.5 text-info-foreground">
                {elementCounts.inputs} inputs
              </span>
            )}
            {elementCounts.forms != null && elementCounts.forms > 0 && (
              <span className="rounded bg-warning-soft border border-warning/30 px-2 py-0.5 text-warning-foreground">
                {elementCounts.forms} forms
              </span>
            )}
            {elementCounts.images != null && elementCounts.images > 0 && (
              <span className="rounded bg-foreground/[0.04] border border-subtle px-2 py-0.5 text-muted-foreground">
                {elementCounts.images} images
              </span>
            )}
          </div>
        </div>
      )}

      {/* Buttons */}
      {buttons.length > 0 && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <MousePointer className="w-3 h-3" /> Buttons ({buttons.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {buttons.filter(b => b.text).map((btn, i) => (
              <span key={i} className="rounded bg-foreground/[0.03] border border-subtle px-2 py-0.5 text-foreground">
                {btn.text}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Form fields */}
      {formFields.length > 0 && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <FormInput className="w-3 h-3" /> Form Fields ({formFields.length})
          </div>
          <div className="space-y-1">
            {formFields.map((field, i) => (
              <div key={i} className="flex items-center gap-2 bg-foreground/[0.03] border border-subtle rounded px-2 py-1">
                <span className="text-foreground font-medium">{field.label || field.name || 'Field'}</span>
                <span className="text-subtle-foreground">{field.type}</span>
                {field.required && <span className="text-destructive">*</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Knowledge summary */}
      {knowledgeSummary && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5">Summary</div>
          <p className="text-muted-foreground leading-relaxed">{knowledgeSummary}</p>
        </div>
      )}
    </div>
  )
}
